import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { exportSectionsToPDF } from '../utils/pdfExport';
import './leakage_eom.css';

const TOTAL = 10;
const footer = 'Invexis Analytics · Inventory Leak End-of-Month Report';

function inr(value) {
  if (value == null) return '—';
  if (typeof value === 'string') return value.startsWith('₹') ? value : `₹${parseFloat(value.replace(/[^0-9.-]+/g,"")).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  return `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function PageFooter({ page }) {
  return <div className="leom-page-footer">{footer}<span>|</span>Page {page} of {TOTAL}</div>;
}

function HatchBar({ title, items }) {
  if (!items || items.length === 0) return null;
  const max = Math.max(...items.map((item) => item.value), 1);
  return (
    <div className="leom-chart">
      <h3>{title}</h3>
      {items.map((item, index) => (
        <div className="leom-bar-row" key={item.label}>
          <span>{item.label}</span>
          <div className="leom-bar-track">
            <i className={`leom-pattern leom-pattern-${index % 6}`} style={{ width: `${(item.value / max) * 100}%` }} />
          </div>
          <strong>{inr(item.value)}</strong>
        </div>
      ))}
    </div>
  );
}

function CoverPage({ cycleDate }) {
  return (
    <section className="leom-page leom-cover" data-leom-section="cover">
      <h1>LEAK</h1>
      <p>End of Month Report</p>
      <span>{cycleDate}</span>
      <PageFooter page={1} />
    </section>
  );
}

function SummaryPage({ kpis, snapshot }) {
  return (
    <section className="leom-page" data-leom-section="summary">
      <p className="leom-eyebrow">01 — Executive Summary</p>
      <h2 className="leom-section-title">Inventory Reconciliation Summary</h2>
      <h3 className="leom-eyebrow leom-gap">The 4 Core Inventory Management Boxes</h3>
      <p className="leom-muted">These macro metrics summarise your total stock movements during the month to reveal overall inventory accuracy and surface hidden losses.</p>
      <div className="leom-kpi-grid">
        {kpis.map((kpi) => (
          <div className="leom-kpi" key={kpi[0]}>
            <span>{kpi[0]}</span>
            <strong>{kpi[1]}</strong>
            <p>{kpi[2]}</p>
          </div>
        ))}
      </div>
      <HatchBar title="KPI Snapshot — Capital Pool vs Accountability vs Leak (INR)" items={snapshot} />
      <p className="leom-lede">This consolidated dashboard brings together your purchases, sales, recorded waste, and physical stock counts into a single view. By combining these metrics, it isolates your true unexplained leakage — showing exactly where inventory is turning into profit, and where it is quietly disappearing.</p>
      <PageFooter page={2} />
    </section>
  );
}

function LedgerPage({ ledger }) {
  const totals = ledger.reduce((sum, row) => ({
    cons: sum.cons + (parseFloat(row[1]) || 0),
    close: sum.close + (parseFloat(row[2]) || 0),
    waste: sum.waste + (parseFloat(row[3]) || 0),
    leak: sum.leak + (parseFloat(row[5]) || 0),
    rev: sum.rev + (parseFloat(row[6]) || 0),
  }), { cons: 0, close: 0, waste: 0, leak: 0, rev: 0 });
  
  return (
    <section className="leom-page" data-leom-section="ledger">
      <p className="leom-eyebrow">02 — Master Ledger</p>
      <h2 className="leom-section-title">Consolidated Master Inventory Reconciliation Ledger</h2>
      <table className="leom-table">
        <thead><tr><th>Ingredient</th><th>Consumed</th><th>Closing Stock</th><th>Waste Cost</th><th>Variance</th><th>Hidden Leak</th><th>Lost Revenue</th><th>Status</th></tr></thead>
        <tbody>{ledger.map((row) => <tr key={row[0]}><td>{row[0]}</td><td>{inr(row[1])}</td><td>{inr(row[2])}</td><td>{inr(row[3])}</td><td>{row[4]}</td><td>{inr(row[5])}</td><td>{row[6] ? inr(row[6]) : '—'}</td><td>{row[7]}</td></tr>)}</tbody>
        <tfoot><tr><td>TOTALS</td><td>{inr(totals.cons)}</td><td>{inr(totals.close)}</td><td>{inr(totals.waste)}</td><td></td><td>{inr(totals.leak)}</td><td>{inr(totals.rev)}</td><td></td></tr></tfoot>
      </table>
      <HatchBar title="Hidden Leakage Value per Ingredient (INR)" items={ledger.filter((row) => row[5] > 0).map((row) => ({ label: row[0], value: row[5] }))} />
      <p className="leom-lede">This table reconciles every ingredient from initial purchase to final state — revealing unexplained variances and unrecorded profit drains in one view.</p>
      <PageFooter page={3} />
    </section>
  );
}

function QuadrantPage({ quadrant, index }) {
  if (!quadrant) return null;
  return (
    <section className="leom-page" data-leom-section={`quadrant-${index}`}>
      <p className="leom-eyebrow">0{index + 3} — Matrix Quadrant {index + 1}</p>
      <h2 className="leom-section-title">Quadrant {index + 1} — {quadrant.title}</h2>
      <p className="leom-italic">{quadrant.sub}</p>
      <p className="leom-lede">{quadrant.text}</p>
      <HatchBar title={`Quadrant ${index + 1} Snapshot (INR)`} items={quadrant.rows.map((row) => ({ label: row[0], value: row[3] || row[1] }))} />
      <table className="leom-table">
        <thead><tr><th>Ingredient</th><th>Consumed Value</th><th>Recorded Waste</th><th>Hidden Leak</th><th>Accuracy Rating</th></tr></thead>
        <tbody>{quadrant.rows.map((row) => <tr key={row[0]}><td>{row[0]}</td><td>{inr(row[1])}</td><td>{inr(row[2])}</td><td>{inr(row[3])}</td><td>{row[4]}</td></tr>)}</tbody>
      </table>
      <PageFooter page={index + 4} />
    </section>
  );
}

function CategoryPage({ cats }) {
  return (
    <section className="leom-page" data-leom-section="category">
      <p className="leom-eyebrow">07 — Category Index</p>
      <h2 className="leom-section-title">Category-Level Leakage & Financial Erosion Index</h2>
      <p className="leom-lede">This section aggregates unexplained ingredient losses directly into your master menu categories. It highlights which prep lines are driving hidden losses.</p>
      <HatchBar title="Category Profit Erosion — Unrecorded Hidden Leakage (INR)" items={cats.map((row) => ({ label: row[0], value: row[3] }))} />
      <table className="leom-table">
        <thead><tr><th>Master Category</th><th>Total Consumption</th><th>Ingredients in Leak</th><th>Hidden Leakage</th><th>Erosion %</th><th>Lost Revenue</th><th>Risk Status</th></tr></thead>
        <tbody>{cats.map((row) => <tr key={row[0]}><td>{row[0]}</td><td>{inr(row[1])}</td><td>{row[2]}</td><td>{inr(row[3])}</td><td>{row[4]}</td><td>{row[5] ? inr(row[5]) : '—'}</td><td>{row[6]}</td></tr>)}</tbody>
      </table>
      <PageFooter page={8} />
    </section>
  );
}

function PerformancePage({ channels }) {
  return (
    <section className="leom-page" data-leom-section="performance">
      <p className="leom-eyebrow">09 — Sales Performance</p>
      <h2 className="leom-section-title">Revenue & Channel Mix</h2>
      <p className="leom-lede">A side-by-side view of gross sales against net realisation, and the category split that produced them.</p>
      <HatchBar title="Revenue by Channel (INR)" items={channels.map(c => ({ label: c[0], value: c[1] }))} />
      <PageFooter page={9} />
    </section>
  );
}

const SVGDefs = () => (
  <svg style={{ position: 'absolute', width: 0, height: 0 }}>
    <defs>
      {/* Pattern 1: Diagonal Stripes */}
      <pattern id="pat-diagonal" width="10" height="10" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="10" stroke="#111" strokeWidth="2.5" />
      </pattern>
      {/* Pattern 2: Vertical Stripes */}
      <pattern id="pat-vertical" width="8" height="8" patternUnits="userSpaceOnUse">
        <line x1="0" y1="0" x2="0" y2="8" stroke="#111" strokeWidth="2" />
      </pattern>
      {/* Pattern 3: Horizontal Stripes */}
      <pattern id="pat-horizontal" width="8" height="8" patternUnits="userSpaceOnUse">
        <line x1="0" y1="0" x2="8" y2="0" stroke="#111" strokeWidth="2" />
      </pattern>
      {/* Pattern 4: Checkered Crosshatch */}
      <pattern id="pat-checkered" width="8" height="8" patternUnits="userSpaceOnUse">
        <rect width="4" height="4" fill="#111" />
        <rect x="4" y="4" width="4" height="4" fill="#111" />
      </pattern>
      {/* Pattern 5: Waves */}
      <pattern id="pat-wave" width="12" height="6" patternUnits="userSpaceOnUse">
        <path d="M 0 3 Q 3 0, 6 3 T 12 3" fill="none" stroke="#111" strokeWidth="1.5" />
      </pattern>
    </defs>
  </svg>
);

function PatternPieChart({ title, segments, total }) {
  const r = 85; // Radius of the pie
  const cx = 100;
  const cy = 100;

  const activeSegments = segments.filter((seg) => seg.value > 0);

  let currentAngle = -Math.PI / 2; // Start at 12 o'clock

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
      <span style={{ fontSize: '15px', fontWeight: '950', color: '#111', textTransform: 'uppercase', letterSpacing: '.06em' }}>
        {title}
      </span>
      <div style={{ position: 'relative', width: '200px', height: '200px' }}>
        <svg width="200" height="200" viewBox="0 0 200 200">
          {total === 0 || activeSegments.length === 0 ? (
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill="#ece9e2"
              stroke="#111"
              strokeWidth="1.5"
            />
          ) : activeSegments.length === 1 ? (
            <circle
              cx={cx}
              cy={cy}
              r={r}
              fill={activeSegments[0].pattern}
              stroke="#111"
              strokeWidth="1.5"
            />
          ) : (
            <>
              {activeSegments.map((seg, idx) => {
                const angleSize = (seg.value / total) * 2 * Math.PI;
                const startAngle = currentAngle;
                const endAngle = currentAngle + angleSize;
                currentAngle = endAngle;

                // Calculate slice path
                const x1 = cx + r * Math.cos(startAngle);
                const y1 = cy + r * Math.sin(startAngle);
                const x2 = cx + r * Math.cos(endAngle);
                const y2 = cy + r * Math.sin(endAngle);
                const largeArcFlag = angleSize > Math.PI ? 1 : 0;
                const pathData = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

                return (
                  <path
                    key={idx}
                    d={pathData}
                    fill={seg.pattern}
                    stroke="#111"
                    strokeWidth="1.5"
                  />
                );
              })}
              {/* Outer boundary stroke */}
              <circle cx={cx} cy={cy} r={r} fill="none" stroke="#111" strokeWidth="1.5" />
            </>
          )}
        </svg>
      </div>
    </div>
  );
}

function ChannelMixPage({ channels }) {
  const totalGross = channels.reduce((sum, c) => sum + (c[1] || 0), 0);
  const patternsList = ['url(#pat-diagonal)', 'url(#pat-vertical)', 'url(#pat-wave)'];
  const channelSegs = channels.map((c, idx) => ({
    label: c[0],
    value: c[1] || 0,
    pattern: patternsList[idx % patternsList.length]
  }));

  const totalNet = channels.reduce((sum, c) => sum + (c[4] || 0), 0);
  const totalComm = channels.reduce((sum, c) => sum + (c[2] || 0), 0);
  const totalDisc = channels.reduce((sum, c) => sum + (c[3] || 0), 0);
  const totalLeakage = Math.max(0, totalGross - totalNet - totalComm - totalDisc);

  const leakageSegs = [
    { label: 'Net Realisation', value: totalNet, pattern: 'url(#pat-checkered)' },
    { label: 'Commission', value: totalComm, pattern: 'url(#pat-horizontal)' },
    { label: 'Discount Funded', value: totalDisc, pattern: 'url(#pat-diagonal)' },
  ];
  if (totalLeakage > 0.01) {
    leakageSegs.push({ label: 'Other Leakage', value: totalLeakage, pattern: 'url(#pat-wave)' });
  }

  return (
    <section className="leom-page" data-leom-section="channel-mix">
      <p className="leom-eyebrow">10 — Channel & Leakage Mix</p>
      <h2 className="leom-section-title">Where Revenue Comes From, Where It Bleeds</h2>
      <p className="leom-lede">The month’s revenue was distributed across order channels, while platform commissions, discounts, packaging, and cancellations explain the gross-to-net gap.</p>
      
      <SVGDefs />
      
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '48px', marginTop: '48px', justifyItems: 'center' }}>
        <PatternPieChart title="Channel Mix" segments={channelSegs} total={totalGross} />
        <PatternPieChart title="Leakage Mix" segments={leakageSegs} total={totalGross} />
      </div>

      {/* Legend Block */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '32px', margin: '32px 0', padding: '16px 0', borderTop: '1px dashed #d8d5cd', borderBottom: '1px dashed #d8d5cd', width: '100%' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <span style={{ fontSize: '11px', fontWeight: '900', color: '#5e6470', textTransform: 'uppercase', letterSpacing: '.08em' }}>Channel Legend</span>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {channelSegs.map((seg) => (
              <div key={seg.label} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '800' }}>
                <svg width="24" height="12" style={{ border: '1px solid #111' }}>
                  <rect width="24" height="12" fill={seg.pattern} />
                </svg>
                <span>{seg.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderLeft: '1px solid #d8d5cd', paddingLeft: '32px' }}>
          <span style={{ fontSize: '11px', fontWeight: '900', color: '#5e6470', textTransform: 'uppercase', letterSpacing: '.08em' }}>Leakage Legend</span>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {leakageSegs.map((seg) => (
              <div key={seg.label} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '800' }}>
                <svg width="24" height="12" style={{ border: '1px solid #111' }}>
                  <rect width="24" height="12" fill={seg.pattern} />
                </svg>
                <span>{seg.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <table className="leom-table">
        <thead><tr><th>Channel</th><th>Gross Revenue</th><th>Commission</th><th>Discount Funded</th><th>Net Realisation</th><th>Realisation %</th></tr></thead>
        <tbody>{channels.map((row) => <tr key={row[0]}><td>{row[0]}</td><td>{inr(row[1])}</td><td>{inr(row[2])}</td><td>{inr(row[3])}</td><td>{inr(row[4])}</td><td>{((row[4] / (row[1] || 1)) * 100).toFixed(1)}%</td></tr>)}</tbody>
      </table>
      <PageFooter page={10} />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Period picker — same month list as the Sales/Expense EOM reports.
// ---------------------------------------------------------------------------
const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const parseDay = (d) => {
  if (!d) return null;
  // Local midnight, never a bare 'YYYY-MM-DD' (read as UTC, printed a day early east of GMT).
  const dateObj = new Date(`${String(d).slice(0, 10)}T00:00:00`);
  return isNaN(dateObj.getTime()) ? null : dateObj;
};

const fmtDay = (d) => {
  const dateObj = parseDay(d);
  if (!dateObj) return '';
  return `${pad2(dateObj.getDate())}/${pad2(dateObj.getMonth() + 1)}/${dateObj.getFullYear()}`;
};

/** Read the business onboarding date cached at login (Businesses.created_at). */
function readOnboardingDate() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const raw = u.businessCreatedAt || u.business_created_at || u.createdAt || u.created_at;
    return raw ? String(raw).slice(0, 10) : '';
  } catch (_) {
    return '';
  }
}

/**
 * "Full period" first (the report's original window: onboarding -> latest closing), then one
 * entry per month from the current month back to the onboarding month. Uncounted months are
 * still listed; the server gates them with its own message.
 */
function buildPeriods(onboardingDate) {
  const options = [{ key: 'all', label: 'Full period · onboarding → last closing', start: '', end: '', isAll: true }];
  const startD = parseDay(onboardingDate);
  if (!startD) return options;

  const today = new Date();
  const endY = today.getFullYear();
  const endM = today.getMonth();
  const months = [];
  let y = startD.getFullYear();
  let m = startD.getMonth();

  while (y < endY || (y === endY && m <= endM)) {
    const isFirst = y === startD.getFullYear() && m === startD.getMonth();
    const isCurrent = y === endY && m === endM;
    const lastDay = new Date(y, m + 1, 0).getDate();
    months.push({
      key: `${y}-${pad2(m + 1)}`,
      label: isCurrent ? `${MONTH_NAMES[m]} ${y} · to date` : `${MONTH_NAMES[m]} ${y}`,
      start: isFirst ? String(onboardingDate).slice(0, 10) : `${y}-${pad2(m + 1)}-01`,
      end: isCurrent ? ymd(today) : `${y}-${pad2(m + 1)}-${pad2(lastDay)}`,
      isCurrent,
    });
    m += 1;
    if (m > 11) { m = 0; y += 1; }
  }
  return [...options, ...months.reverse()];
}

export default function LeakageEom({ goTo }) {
  const businessId = localStorage.getItem('businessId');
  const token = localStorage.getItem('token');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [gated, setGated] = useState(false);
  // Gated because Kitchen Leakage has not calculated the period (kitchen-sourced businesses),
  // rather than because no closing stock was given.
  const [kitchenPending, setKitchenPending] = useState(false);
  // Lets the owner close the gate card and pick another month from the toolbar behind it.
  const [gateDismissed, setGateDismissed] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [onboardingDate, setOnboardingDate] = useState(readOnboardingDate);
  const [selectedKey, setSelectedKey] = useState('all');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const rootRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const periods = useMemo(() => buildPeriods(onboardingDate), [onboardingDate]);
  const selected = useMemo(
    () => periods.find((p) => p.key === selectedKey) || periods[0],
    [periods, selectedKey]
  );
  const reqStart = selected && !selected.isAll ? selected.start : '';
  const reqEnd = selected && !selected.isAll ? selected.end : '';

  useEffect(() => {
    if (!businessId || !token) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(false);
    setGated(false);
    setKitchenPending(false);
    setGateDismissed(false);

    // No dates: the server defaults to onboarding -> latest confirmed closing stock, and
    // gates the report until closing stock has been given at least once. A chosen month
    // sends its own window, gated on the count that closes THAT month.
    const qs = new URLSearchParams({ businessId });
    if (reqStart && reqEnd) {
      qs.set('startDate', reqStart);
      qs.set('endDate', reqEnd);
    }
    axios.get(`/api/reports/leakage-eom?${qs.toString()}`, {
      headers: { Authorization: `Bearer ${token}` }
    })
    .then(res => {
      if (cancelled) return;
      const body = res.data || {};
      // Every response carries the onboarding date, gated or not — fills the month list
      // when the login cache never held one.
      const respOnboarding = body.period?.onboardingDate || body.data?.period?.onboardingDate;
      if (respOnboarding) {
        setOnboardingDate((prev) => prev || String(respOnboarding).slice(0, 10));
      }
      if (body.gated) {
        setGated(true);
        setKitchenPending(Boolean(body.kitchenPending));
        setData(null);
        setLoading(false);
        return;
      }
      const p = (body.data && body.data.period) || {};
      const cycleDate = p.startDate && p.endDate ? `${fmtDay(p.startDate)} – ${fmtDay(p.endDate)}` : '';
      setData({ ...body.data, cycleDate });
      setLoading(false);
    })
    .catch(err => {
      if (cancelled) return;
      console.error('Error fetching leakage EOM report', err);
      setData(null);
      setError(true);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [businessId, token, reqStart, reqEnd]);

  const downloadPdf = async () => {
    if (!rootRef.current || exporting) return;
    setExporting(true);
    try {
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      const sectionSelectors = [
        '[data-leom-section="cover"]',
        '[data-leom-section="summary"]',
        '[data-leom-section="ledger"]',
        ...(data?.quadrants || []).map((_, idx) => `[data-leom-section="quadrant-${idx}"]`),
        '[data-leom-section="category"]',
        '[data-leom-section="performance"]',
        '[data-leom-section="channel-mix"]',
      ];
      await exportSectionsToPDF({
        container: rootRef.current,
        sectionSelectors,
        title: 'Leakage EOM Report',
        periodLabel: data?.cycleDate || '',
        fullPageMode: true,
        forcePageBreakPerSection: true,
        hideSelectorsDuringCapture: ['.leom-toolbar'],
      });
    } catch (e) {
      alert('Could not generate PDF: ' + e.message);
    } finally {
      setExporting(false);
    }
  };

  // The toolbar (and its period picker) sits above every state — report, gate, loading and
  // error — so a gated month is never a dead end.
  const toolbar = (
    <div className="leom-toolbar">
      <div className="leom-toolbar-left">
        {goTo && (
          <button
            type="button"
            className="leom-back-btn"
            onClick={() => goTo('reports')}
            aria-label="Back to Reports"
          >
            ← Back to Reports
          </button>
        )}
        <span className="leom-toolbar-title">Leakage EOM Report</span>
        <div className="leom-period">
          <span className="leom-period-label">Period</span>
          <div className="leom-dropdown-container" ref={dropdownRef}>
            <button
              type="button"
              className="leom-dropdown-trigger"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              aria-haspopup="listbox"
              aria-expanded={dropdownOpen}
            >
              {selected?.label || 'Select period'}
              <span className="leom-dropdown-arrow">▾</span>
            </button>
            {dropdownOpen && (
              <div className="leom-dropdown-menu" role="listbox">
                {periods.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    role="option"
                    aria-selected={selectedKey === option.key}
                    className={`leom-dropdown-item${
                      selectedKey === option.key ? ' leom-dropdown-item-active' : ''
                    }`}
                    onClick={() => {
                      setSelectedKey(option.key);
                      setDropdownOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {!loading && data && data.cycleDate && <span className="leom-toolbar-badge">{data.cycleDate}</span>}
      </div>
      <button
        type="button"
        className="leom-download-btn"
        onClick={downloadPdf}
        disabled={exporting || loading || !data}
      >
        {exporting ? 'Preparing PDF…' : '⭳ Download PDF'}
      </button>
    </div>
  );

  const shell = (children) => (
    <main className={`leom-root${exporting ? ' leom-exporting' : ''}`} ref={rootRef}>
      {toolbar}
      {children}
    </main>
  );

  const periodName = selected && !selected.isAll ? selected.label : 'this period';

  if (gated && kitchenPending) {
    // This business's report is Kitchen Leakage's figures, so a period Kitchen Leakage has
    // not calculated is opened there — never filled in with a second, different number.
    if (gateDismissed) {
      return shell(
        <div className="leom-state">
          Kitchen Leakage hasn’t been calculated for {periodName}. Pick another period above, or
          run it in Kitchen Leakage to open this one.
        </div>
      );
    }
    return shell(
      <div className="leom-gate">
        <div className="leom-gate-card">
          <button
            type="button"
            className="leom-gate-close"
            aria-label="Close"
            onClick={() => setGateDismissed(true)}
          >
            ✕
          </button>
          <div className="leom-gate-icon">🍳</div>
          <h3>Kitchen Leakage not calculated</h3>
          <p>
            This report shows the same ingredients and figures as Kitchen Leakage. Run Kitchen
            Leakage for {periodName} and this report will open with its numbers.
          </p>
          <button
            type="button"
            className="leom-gate-primary"
            onClick={() => goTo && goTo('leakage-monthly')}
          >
            Open Kitchen Leakage
          </button>
        </div>
      </div>
    );
  }

  if (gated) {
    // Never having counted is fixed by counting; an uncounted month is fixed by counting OR
    // by picking a month that was counted — so the two get different words.
    const monthGate = Boolean(selected && !selected.isAll);
    if (gateDismissed) {
      return shell(
        <div className="leom-state">
          Closing stock isn’t submitted for {periodName}. Pick another period above, or give
          closing stock to open this one.
        </div>
      );
    }
    return shell(
      <div className="leom-gate">
        <div className="leom-gate-card">
          <button
            type="button"
            className="leom-gate-close"
            aria-label="Close"
            onClick={() => setGateDismissed(true)}
          >
            ✕
          </button>
          <div className="leom-gate-icon">🔒</div>
          <h3>Closing stock required</h3>
          <p>
            {monthGate
              ? `${selected.label} has no confirmed closing stock count, so there is nothing
                 reconciled to report for it. Pick a month that was counted, or give closing
                 stock for this one.`
              : `The Leakage End-of-Month report can only be generated after you submit closing
                 stock. Once you give closing stock, this report opens automatically and the last
                 count carries forward until you submit a new one.`}
          </p>
          <button
            type="button"
            className="leom-gate-primary"
            onClick={() => goTo && goTo('closing-stock')}
          >
            Give Closing Stock
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return shell(<div className="leom-state">Loading Dynamic Leakage Calculations...</div>);
  }

  if (error || !data) {
    return shell(<div className="leom-state">Could not load the leakage report for {periodName}. Please try again.</div>);
  }

  return shell(
    <>
      <CoverPage cycleDate={data.cycleDate} />
      <SummaryPage kpis={data.kpis} snapshot={data.snapshot} />
      <LedgerPage ledger={data.ledger} />
      {data.quadrants.map((quadrant, index) => <QuadrantPage key={quadrant.title} quadrant={quadrant} index={index} />)}
      <CategoryPage cats={data.cats} />
      <PerformancePage channels={data.channels} />
      <ChannelMixPage channels={data.channels} />
    </>
  );
}
